import type { WorkflowInput } from "@/stores/workflowsStore";
import { resolvePropsInPayload } from "@/lib/workflow/resolveProps";

export { resolvePropsInPayload };

// ============================================================================
// Schema Types
// ============================================================================

export interface SchemaProperty {
  type?: string;
  description?: string;
  default?: unknown;
  const?: unknown;
  enum?: unknown[];
  examples?: unknown[];
  minimum?: number;
  properties?: Record<string, SchemaProperty>;
  readOnly?: boolean;
}

export interface ResultSchema {
  type: "object";
  properties: Record<string, SchemaProperty>;
  required?: string[];
  additionalProperties?: boolean;
}

// ============================================================================
// Trigger Sample Overrides (per-trigger customization)
// ============================================================================

export interface TriggerSampleOverrides {
  /** Override value generation for a specific key. Return undefined to fall through to default. */
  getValueOverride?: (
    key: string,
    prop: SchemaProperty,
    triggerPayload?: Record<string, unknown>,
  ) => unknown | undefined;
  /** Return true to skip a key entirely from sample output. */
  skipKey?: (key: string, prop: SchemaProperty, triggerPayload?: Record<string, unknown>) => boolean;
}

/** Fields that are auto-generated by the backend and should not be shown */
export const AUTO_GENERATED_TRIGGER_FIELDS = ["triggeredAt", "payment"];

// ============================================================================
// Core Sample Generation
// ============================================================================

export function generateSampleFromSchema(
  schema: ResultSchema | undefined,
  triggerType: string,
  triggerPayload?: Record<string, unknown>,
  overrides?: TriggerSampleOverrides,
): Record<string, unknown> {
  if (!schema?.properties) return {};

  const sample: Record<string, unknown> = {};

  for (const [key, prop] of Object.entries(schema.properties)) {
    if (AUTO_GENERATED_TRIGGER_FIELDS.includes(key)) continue;
    if (prop.readOnly) continue;
    if (overrides?.skipKey?.(key, prop, triggerPayload)) continue;

    // Check for trigger-specific override first
    const overrideValue = overrides?.getValueOverride?.(key, prop, triggerPayload);
    if (overrideValue !== undefined) {
      sample[key] = overrideValue;
      continue;
    }

    sample[key] = generateValueForProperty(key, prop, triggerType, triggerPayload, overrides);
  }

  return sample;
}

export function generateValueForProperty(
  key: string,
  prop: SchemaProperty,
  triggerType: string,
  triggerPayload?: Record<string, unknown>,
  overrides?: TriggerSampleOverrides,
): unknown {
  if (prop.default !== undefined) return prop.default;
  if (prop.const !== undefined) return prop.const;
  if (prop.enum && Array.isArray(prop.enum) && prop.enum.length > 0) return prop.enum[0];

  if (prop.examples && Array.isArray(prop.examples) && prop.examples.length > 0) return prop.examples[0];

  if (prop.type === "object" && prop.properties) {
    const nested: Record<string, unknown> = {};
    for (const [nestedKey, nestedProp] of Object.entries(prop.properties) as [string, SchemaProperty][]) {
      if (nestedProp.readOnly) continue;
      nested[nestedKey] = generateValueForProperty(nestedKey, nestedProp, triggerType, triggerPayload, overrides);
    }
    return nested;
  }

  const sample = generateByFieldName(key, prop);
  if (sample !== undefined) return coerceToSchemaType(sample, prop);

  return getTypeDefault(prop);
}

// ============================================================================
// Field Name Heuristics
// ============================================================================

function generateByFieldName(key: string, prop: SchemaProperty): unknown {
  const k = key.toLowerCase();

  if (k.includes("address")) return "0xb3b32f9f8827d4634fe7d973fa1034ec9fddb3b3";
  if (k === "from" || k === "to" || k === "recipient") {
    const desc = (prop.description || "").toLowerCase();
    if (desc.includes("email") && prop.type === "array") return ["user@example.com"];
    if (desc.includes("email")) return "user@example.com";
    if (prop.type === "string") return "0xb3b32f9f8827d4634fe7d973fa1034ec9fddb3b3";
  }
  if (k.includes("hash") || k === "conditionid" || k === "marketid") {
    return "0xdcde0e55e215088cca1778be4ff67370d0a6f333eb867f67ea6cead972e0c541";
  }
  if (k === "id" || k.endsWith("id")) {
    if (prop.type === "number" || prop.type === "integer") return 123456789;
    return "sample_id_123abc";
  }
  if (k === "price") return 0.65;
  if (k === "size" || k === "amount" || k === "value") return 100;
  if (k === "side") return "BUY";
  if (k === "question" || k === "title") return "Sample question or title?";
  if (k === "slug") return "sample-slug-abc123";
  if (k === "category") return "general";
  if (k === "outcomes" && prop.type === "array") return ["Yes", "No"];
  if (k === "liquidity") return 50000;
  if (k === "text") return "hello";
  if (k === "username") return "sample_user";
  if (k.includes("timestamp") || k.includes("date") || k === "enddate") {
    if (prop.type === "number" || prop.type === "integer") return Math.floor(Date.now() / 1000);
    return new Date().toISOString();
  }
  if (k === "blocknumber") return 25643730;
  if (k === "logindex") return 0;
  if (k === "topics") return ["0x0000000000000000000000000000000000000000000000000000000000000000"];
  if (k === "data") return "0x";

  return undefined;
}

// ============================================================================
// Type Helpers
// ============================================================================

function getTypeDefault(prop: SchemaProperty): unknown {
  switch (prop.type) {
    case "number":
    case "integer":
      return prop.minimum ?? 0;
    case "string":
      return "";
    case "boolean":
      return false;
    case "array":
      return [];
    case "object":
      return {};
    default:
      return null;
  }
}

function coerceToSchemaType(value: unknown, prop: SchemaProperty): unknown {
  if (value === undefined || value === null || !prop?.type) return value;

  const schemaType = prop.type;
  const actualType = typeof value;

  if (schemaType === "string" && actualType === "string") return value;
  if ((schemaType === "number" || schemaType === "integer") && actualType === "number") return value;
  if (schemaType === "boolean" && actualType === "boolean") return value;
  if (schemaType === "array" && Array.isArray(value)) return value;
  if (schemaType === "object" && actualType === "object" && !Array.isArray(value)) return value;

  if (schemaType === "string" && actualType === "number") return String(value);
  if ((schemaType === "number" || schemaType === "integer") && actualType === "string") {
    const n = Number(value);
    if (!isNaN(n)) return schemaType === "integer" ? Math.floor(n) : n;
  }
  if (schemaType === "string" && actualType === "boolean") return String(value);
  if ((schemaType === "number" || schemaType === "integer") && actualType === "boolean") return value ? 1 : 0;

  return value;
}

// ============================================================================
// Input Schema Sample Generation (for manual triggers)
// ============================================================================

export function generateSampleFromInputSchema(inputs: WorkflowInput[]): Record<string, unknown> {
  const sample: Record<string, unknown> = {};
  for (const input of inputs) {
    if (!input.key) continue;
    switch (input.type) {
      case "number":
        sample[input.key] = 0;
        break;
      case "boolean":
        sample[input.key] = false;
        break;
      case "object":
        sample[input.key] = {};
        break;
      case "array":
        sample[input.key] = [];
        break;
      default:
        sample[input.key] = "";
    }
  }
  return sample;
}
